import { useMemo } from "react";
import { useAppSelector } from "app/store";
import { getGaiaObjectCategory, parseType } from "utils/gaiaobject-utils";
import { useGetUIContentQuery } from "./endpoints";
import { applyUiContent, initialState } from "./slice";
import { IUiContentState } from "./definitions";

export const useGaiaObjectUiContent = (rawType?: string) => {
  const type = parseType(rawType);

  // Simply select from already-loaded data (loaded by Header component)
  const litigationUiContent = useAppSelector(
    (state) => state.litigationUiContent,
  );
  const noticeUiContent = useAppSelector((state) => state.noticeUiContent);
  const defaultUiContent = useAppSelector((state) => state.uiContent);

  if (type === "litigation") return litigationUiContent;
  if (type === "notice") return noticeUiContent;
  return defaultUiContent;
};

// Keyed by getGaiaObjectCategory's output. An id of no known category yields no page, so
// the query is skipped rather than falling back to some other team's audit content.
const PAGE_BY_CATEGORY: Record<string, string | undefined> = {
  audit: "createaudit",
  notices: "notice",
  litigations: "litigation",
};

// Member and guest lists describe the viewer, not the resource, so they stay viewer-scoped.
const VIEWER_SCOPED_FIELDS = [
  "ownerOptions",
  "criticalOwnerOptions",
  "teamAndAllGuestOwnerOptions",
  "teamAndCurResourceGuestOwnerOptions",
] as const;

/** UI content for a task, taken from the team that owns its parent. */
export const useTaskUiContent = (
  gaiaObjectId?: string,
  ownerTeamName?: string | null,
) => {
  const category = getGaiaObjectCategory(gaiaObjectId || "");
  const page = PAGE_BY_CATEGORY[category] ?? "";
  const teamName = ownerTeamName || "";

  const { data } = useGetUIContentQuery(
    { teamName, page },
    { skip: !teamName || !page },
  );

  const viewerUiContent = useGaiaObjectUiContent(category);

  return useMemo(() => {
    if (!data) {
      return viewerUiContent;
    }
    const content: IUiContentState = { ...initialState };
    applyUiContent(content, data.content);
    VIEWER_SCOPED_FIELDS.forEach((field) => {
      content[field] = viewerUiContent[field];
    });
    return content;
  }, [data, viewerUiContent]);
};
